<script>
	import { tick } from "svelte";

	import model from "../../stores/modelStore";
	import lookups from "../../stores/lookupStore";
	import { registerPageComplete } from "../../tealium";
	import { projectSettings, acceptedFileUploadList } from "../../config";
	import { isValid, validator } from "../../validation";

	import Form from "../../components/Form.svelte";
	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import RadioGroup from "../../components/RadioGroup.svelte";
	import TextArea from "../../components/TextArea.svelte";
	import DatePicker from "../../components/DatePicker.svelte";
	import Checkbox from "../../components/Checkbox.svelte";
	import NumberInput from "../../components/NumberInput.svelte";
	import MultiSelectAutoComplete from "../../components/MultiSelectAutoComplete.svelte";
	import DropZone from "../../components/DropZone.svelte";
	import PageSection from "../../components/PageSection.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";

	// props
	export let persistUrl = null;
	export let pageName = null;
	export let pageTitle = null;
	export let disabled = false;
	export let lastVisitedPage = null;

	// component state updating
	if (!disabled) {
		$model.lastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}

	$model.claimDetails.medications = $model.claimDetails.medications || [];

	let buttons = null;
	let startEndYearValidator = null;
	let otherMedication = $lookups.medication.filter((m) => m.label.startsWith("Other"))[0];
	let options = {
		url: "/api/file-upload?token=" + $model.registrationToken,
		acceptedFiles: acceptedFileUploadList,
		fileUploadToken: $model.registrationToken
	};

	const handleSubmit = async (e) => {
		buttons.blockNext();

		let errors = isValid();

		if (!errors.length) {
			let response = await model.saveModelAsync(persistUrl);

			if (!response.success) {
				console.log(displayError);
			} else {
				await registerPageComplete(pageName, $model.contact.emailAddress, $model.contact.phoneNumber);

				buttons.next();
			}
		} else {
			errors[0].scrollIntoView({ behavior: "smooth" });
		}

		buttons.unblock();
	};

	async function handleStartEndYearChange() {
		// Wait for the DOM to update after selectedItems changes
		await tick();

		// trigger change for validation
		if (startEndYearValidator) {
			const event = new Event("change", { bubbles: true }); // Ensure event bubbles up if needed
			startEndYearValidator.dispatchEvent(event);
		}
	}

	let validateStartEndYears = 0;
	const minYear = 1920;
	const maxYear = new Date().getFullYear();
	function getYearDifference(startYear, endYear) {
		if (!startYear || !endYear) {
			return 0;
		}
		if (startYear < minYear || startYear > maxYear || endYear < minYear || endYear > maxYear) {
			return 0;
		}

		return endYear - startYear;
	}

	async function handleStillUsingChange(e) {
		if (e.target.checked) {
			$model.claimDetails.yearEnd = null;

			let endYear = document.getElementById("endYear");
			$validator.clearVisualError(endYear);
		}
	}

	$: validateStartEndYears = getYearDifference($model.claimDetails.yearStart, $model.claimDetails.yearEnd);
	$: showOtherMedication = $model.claimDetails.medications.filter((m) => m.value == otherMedication.value).length > 0;
	$: showDropZone = $model.claimDetails.hasDocumentation === "Yes" && $model.claimDetails.exposedToParaquat === "Yes";

	console.log("showOtherMedication", showOtherMedication);
	console.log("model.claimDetails.medications", $model.claimDetails.medications);
	console.log("otherMedication", otherMedication);

	$model.documentList = $model.documentList || [];

</script>

<PageSection>
	<Form {disabled} id="form-claim-details" on:submit={handleSubmit}>
		<FormRow class="justify-content-center confirmation-hide">
			<FormColumn>
				<h4 class="text-center">{@html projectSettings.classActionName}</h4>
				<h1 class="text-center">Claim Details</h1>
			</FormColumn>
		</FormRow>

		<hr class="primary" />

		<div class="confirmation-box-shadow">
			<FormRow>
				<FormColumn>
					<YesNoGroup
						required
						{disabled}
						name="exposedToParaquat"
						label="Have you ever been exposed to the herbicide paraquat, either through your work (such as farming or landscaping) or through any other type of contact?"
						bind:value={$model.claimDetails.exposedToParaquat} />
				</FormColumn>
			</FormRow>

			{#if $model.claimDetails.exposedToParaquat === "Yes"}
				<FormRow>
					<FormColumn>
						<YesNoGroup
							required
							{disabled}
							name="provideApproxDates"
							label="Can you provide the approximate time period during which you were exposed to paraquat?"
							bind:value={$model.claimDetails.provideApproxDates} />
					</FormColumn>
				</FormRow>

				{#if $model.claimDetails.provideApproxDates === "Yes"}
					<FormRow class="align-items-end">
						<FormColumn cols="5">
							<NumberInput
								required
								{disabled}
								label="Start Year?"
								minvalue={minYear}
								maxvalue={maxYear}
								bind:value={$model.claimDetails.yearStart}
								on:change={handleStartEndYearChange} />
							<input
								type="text"
								bind:this={startEndYearValidator}
								hidden
								value={validateStartEndYears}
								minvalue="0"
								data-bouncer-message="The End Year must be equal or greater than the Start Year" />
						</FormColumn>
						<FormColumn cols="5">
							<NumberInput
								id="endYear"
								required={!$model.claimDetails.stillUsing}
								disabled={disabled || $model.claimDetails.stillUsing}
								label="End Year?"
								minvalue={minYear}
								maxvalue={maxYear}
								bind:value={$model.claimDetails.yearEnd}
								on:change={handleStartEndYearChange} />
						</FormColumn>
						<FormColumn cols="2">
							<Checkbox
								{disabled}
								bind:checked={$model.claimDetails.stillUsing}
								label="Still using"
								id="stillUsing"
								on:change={handleStillUsingChange}>
								></Checkbox>
						</FormColumn>
					</FormRow>
				{/if}

				<FormRow>
					<FormColumn>
						<RadioGroup
							required
							{disabled}
							name="diagnosedParkinsons"
							label="Have you been officially diagnosed with Parkinson's disease by a medical professional?"
							optionList={[
								{ label: "Yes", value: "Yes" },
								{ label: "No", value: "No" },
								{ label: "Currently seeking diagnosis", value: "Seeking" },
								{ label: "Other", value: "Other" }
							]}
							bind:value={$model.claimDetails.diagnosedParkinsons} />
					</FormColumn>
				</FormRow>

				{#if $model.claimDetails.diagnosedParkinsons === "Yes"}
					<FormRow>
						<FormColumn cols="6">
							<DatePicker
								required
								{disabled}
								name="dateDiagnosedParkinsons"
								label="When were you diagnosed?"
								bind:formattedSelected={$model.claimDetails.dateDiagnosedParkinsons}
								validator={true}
								start={new Date(1980, 0, 1)}
								end={new Date()}
								openOnClass="calendar-icon" />
						</FormColumn>
					</FormRow>
				{/if}

				{#if $model.claimDetails.diagnosedParkinsons === "Other"}
					<FormRow>
						<FormColumn>
							<TextArea
								{disabled}
								label="Please specify"
								bind:value={$model.claimDetails.diagnosedParkinsonsDetails} />
						</FormColumn>
					</FormRow>
				{/if}

				{#if $model.claimDetails.diagnosedParkinsons === "No" || $model.claimDetails.diagnosedParkinsons === "Seeking"}
					<FormRow>
						<FormColumn>
							<RadioGroup
								required
								{disabled}
								name="treatedForSimilar"
								label="If you have not received an official diagnosis, are you currently being treated for symptoms similar to Parkinson's disease (such as tremors, stiffness, or balance issues)?"
								optionList={[
									{ label: "Yes", value: "Yes" },
									{ label: "No", value: "No" },
									{ label: "Other", value: "Other" }
								]}
								bind:value={$model.claimDetails.treatedForSimilar} />
						</FormColumn>
					</FormRow>

					{#if $model.claimDetails.treatedForSimilar === "Other"}
						<FormRow>
							<FormColumn>
								<TextArea
									{disabled}
									label="Please specify"
									bind:value={$model.claimDetails.treatedForSimilarDetails} />
							</FormColumn>
						</FormRow>
					{/if}
					{#if $model.claimDetails.treatedForSimilar === "Yes"}
						<FormRow>
							<FormColumn>
								<MultiSelectAutoComplete
									label="If you are receiving treatment, can you provide the name(s) of the medication(s) prescribed to you?"
									placeholder="Search for medications..."
									optionList={$lookups.medication}
									bind:selectedItems={$model.claimDetails.medications}
									otherOption={otherMedication}
									labelProperty="label"
									valueProperty="value"
									required={true}
									{disabled} />
							</FormColumn>
						</FormRow>

						{#if showOtherMedication}
							<FormRow>
								<FormColumn>
									<TextArea
										required
										{disabled}
										label="Please provide the names of other medication(s)"
										bind:value={$model.claimDetails.otherMedicationDetails} />
								</FormColumn>
							</FormRow>
						{/if}
					{/if}
				{/if}

				<FormRow>
					<FormColumn>
						<YesNoGroup
							required
							{disabled}
							name="hasDocumentation"
							label="Do you have any documentation that supports your paraquat exposure - such as employment records, or purchase receipts?"
							bind:value={$model.claimDetails.hasDocumentation} />
					</FormColumn>
				</FormRow>
			{/if}

			<div hidden={showDropZone ? null : true}>
				<FormRow>
					<FormColumn>
						<label for="fileUpload">File Upload</label>
						<DropZone
							{disabled}
							{options}
							id="dropzone"
							message="Upload files for your registration."
							bind:documentList={$model.documentList}>
						</DropZone>
					</FormColumn>
				</FormRow>
			</div>

			{#if $model.claimDetails.exposedToParaquat}
				<FormRow>
					<FormColumn>
						<TextArea
							{disabled}
							label="If there is any additional information you would like to provide, please provide that information here."
							bind:value={$model.claimDetails.additionalInformation} />
					</FormColumn>
				</FormRow>
			{/if}
		</div>

		<FormRow class="confirmation-hide">
			<FormColumn>
				<NavigationButtons bind:buttons disableNext={$model.claimDetails.purchasedSubscription === "No"} />
			</FormColumn>
		</FormRow>
	</Form>
</PageSection>
